import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './News.css'; // You should create a new CSS file for news styling.
import useNews from '../../hooks/useNews';
import LinkedInPosts from '../LinkedInPosts';
import Footer from '../Footer';

function linkifyText(text) {
  if (!text) return null;
  return text.split(/(https?:\/\/[^\s]+)/g).map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="article-inline-link-news"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

function News() {
  const { news, loading, error } = useNews();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Get the latest news article (first in the news array from Supabase)
  const latestNews = news.length > 0 ? news[0] : null;
  
  // Get all other news articles (excluding the latest)
  const otherNews = news.slice(1);
  
  // Calculate how many cards to show (3 per row)
  const cardsPerRow = 3;
  const totalCards = otherNews.length;
  const maxIndex = Math.max(0, totalCards - cardsPerRow);
  
  const nextCards = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.min(prev + cardsPerRow, maxIndex));
    setTimeout(() => setIsTransitioning(false), 120);
  };
  
  const prevCards = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.max(prev - cardsPerRow, 0));
    setTimeout(() => setIsTransitioning(false), 120);
  };
  
  const visibleCards = otherNews.slice(currentIndex, currentIndex + cardsPerRow);

  // Loading state
  if (loading) {
    return (
      <div className="news-page-news">
        <div className="news-header-news">
          <div className="wall-container-news">
            <video className="bg-video-news" autoPlay muted loop playsInline>
              <source src={process.env.PUBLIC_URL + '/videos/video1.mp4'} />
            </video>
          </div>
          <h1>News</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading news...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="news-page-news">
        <div className="news-header-news">
          <div className="wall-container-news">
            <video className="bg-video-news" autoPlay muted loop playsInline>
              <source src={process.env.PUBLIC_URL + '/videos/video1.mp4'} />
            </video>
          </div>
          <h1>News</h1>
        </div>
        <div className="error-container">
          <p>Failed to load news: {error.message}</p>
        </div>
      </div>
    );
  }

  // No news state
  if (!latestNews) {
    return (
      <div className="news-page-news">
        <div className="news-header-news">
          <div className="wall-container-news">
            <video className="bg-video-news" autoPlay muted loop playsInline>
              <source src={process.env.PUBLIC_URL + '/videos/video1.mp4'} />
            </video>
          </div>
          <h1>News</h1>
        </div>
        <div className="error-container">
          <p>No news articles available</p>
        </div>

        {/* LinkedIn Posts Section */}
        <LinkedInPosts />
      </div>
    );
  }

  return (
    <>
    <div className="news-page-news">
      {/* Header Section */}
      <div className="news-header-news">
        <div className="wall-container-news">
          <video className="bg-video-news" autoPlay muted loop playsInline>
            <source src={process.env.PUBLIC_URL + '/videos/video1.mp4'} />
          </video>
        </div>
        <h1>News</h1>
      </div>

      {/* Latest News Section */}
      <div className="latest-news-section-news">
        <div className="latest-news-container-news">
          <div className="latest-news-image-news">
            <img src={latestNews.image} alt="Latest News" />
          </div>
          <div className="latest-news-content-news">
            <p className="latest-news-date-news">{latestNews.date}</p>
            <Link to={latestNews.link} className="latest-news-title-news">
              {latestNews.title}
            </Link>
            <p className="latest-news-description-news">{linkifyText(latestNews.content)}</p>
          </div>
        </div>
      </div>

      {/* Other News Section */}
      <div className="other-news-section-news">
        <h2 className="other-news-title-news">
          <Link to="/news/all" className="other-news-title-link">
            Other News
            <span className="other-news-title-link__arrow" aria-hidden="true">→</span>
            <span className="sr-only"> (view all news)</span>
          </Link>
        </h2>
        
        <div className="other-news-container-news">
          {/* Previous Arrow */}
          {currentIndex > 0 && (
            <button 
              className={`news-nav-arrow-news prev-arrow-news ${isTransitioning ? 'disabled-news' : ''}`} 
              onClick={prevCards}
              disabled={isTransitioning}
            >
              <span>‹</span>
            </button>
          )}
          
          {/* News Cards */}
          <div className={`news-cards-grid-news ${isTransitioning ? 'transitioning-news' : ''}`}>
            {visibleCards.map((article) => (
              <Link 
                to={article.link}
                className="news-card-link-news" 
                key={article.id}
              >
                <div className="news-card-news">
                  <div className="news-card-image-news">
                    <img src={article.image} alt={article.title} />
                  </div>
                  <div className="news-card-content-news">
                    <p className="news-card-date-news">{article.date}</p>
                    <h3 className="news-card-title-news">{article.title}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          {/* Next Arrow */}
          {currentIndex < maxIndex && (
            <button 
              className={`news-nav-arrow-news next-arrow-news ${isTransitioning ? 'disabled-news' : ''}`} 
              onClick={nextCards}
              disabled={isTransitioning}
            >
              <span>›</span>
            </button>
          )}
        </div>
      </div>

      {/* LinkedIn Posts Section */}
      <LinkedInPosts />
    </div>
        <Footer />
        </>
  );
}

export default News;